import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Divider, Select } from "antd";
import { DollarOutlined, SaveOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { viewCountryList } from "../../Redux/Actions/settingsActions";
import "./addAgentProfitRange.css";

const { Option } = Select;

const formatPriceDisplay = (value) => {
  const num = parseFloat(value) || 0;
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(1)} Cr`;
  } else if (num >= 100000) {
    return `${(num / 100000).toFixed(1)} L`;
  }
  return num.toLocaleString();
};

const AgentProfitRangeEditModal = ({
  visible,
  onClose,
  profitRangeData,
  onSave,
  enumsData = {},
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [pricePreview, setPricePreview] = useState({ min: '', max: '' });

  const { countryList } = useSelector((state) => state.country);
  const countries = countryList?.data || [];

  // Real-time price preview
  const updatePricePreview = () => {
    const minPrice = form.getFieldValue('min_price');
    const maxPrice = form.getFieldValue('max_price');
    setPricePreview({
      min: formatPriceDisplay(minPrice),
      max: formatPriceDisplay(maxPrice)
    });
  };

  useEffect(() => {
    if (visible) {
      dispatch(viewCountryList({ page: 1, limit: 10, filter: "" }));
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (visible && profitRangeData) {
      form.setFieldsValue(profitRangeData);
      // Update preview on edit load
      updatePricePreview();
    } else if (visible) {
      form.resetFields();
      setPricePreview({ min: '', max: '' });
    }
  }, [visible, profitRangeData, form]);

  useEffect(() => {
    const handleChange = () => updatePricePreview();
    const fields = ['min_price', 'max_price'];
    fields.forEach(field => {
      form.getFieldInstance(field)?.input?.addEventListener('input', handleChange);
    });
    return () => {
      fields.forEach(field => {
        form.getFieldInstance(field)?.input?.removeEventListener('input', handleChange);
      });
    };
  }, [form]);

  const handleFinish = (values) => {
    onSave(values);
  };

  return (
    <Modal
      title={
        <div className="profit-range-edit-modal-header-content">
          <DollarOutlined className="profit-range-edit-modal-header-icon" style={{ color: "white" }} />
          <span style={{ color: "white" }}>
            {profitRangeData?._id ? "EDIT PROFIT RANGE" : "ADD PROFIT RANGE"}
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={600}
      className="profit-range-edit-modal-override"
      styles={{
        header: { background: "#0D152B", borderRadius: "12px 12px 0 0", padding: "20px 24px", borderBottom: "none" },
        body: { padding: "24px", background: "#fafafa" },
      }}
    >
      <div className="profit-range-edit-modal-form">
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <div className="profit-range-edit-section">
            <h3 className="profit-range-edit-section-header">Profit Range Details</h3>
            <div className="profit-range-edit-grid">
              <Form.Item label="Profit Range (%)" name="agent_profit_range" rules={[{ required: true, message: "Please input profit range" }]}>
                <Input type="number" size="large" placeholder="Enter profit range (e.g. 5)" className="profit-range-edit-input-override" step="0.01" min="0" />
              </Form.Item>

              <Form.Item label="Min Price" name="min_price" rules={[{ required: true, message: "Please input min price" }]}>
                <Input
                  type="number"
                  size="large"
                  placeholder="Enter min price"
                  className="profit-range-edit-input-override"
                  min="0"
                  onChange={updatePricePreview}
                />
                {pricePreview.min && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontWeight: 500 }}>
                    Preview: ₹{pricePreview.min}
                  </div>
                )}
              </Form.Item>

              <Form.Item label="Max Price" name="max_price" rules={[
                { required: true, message: "Please input max price" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const minPrice = getFieldValue('min_price');
                    if (!value || value === '' || !minPrice || minPrice === '' || parseFloat(value) > parseFloat(minPrice)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Max price must be greater than min price'));
                  },
                }),
              ]}>
                <Input
                  type="number"
                  size="large"
                  placeholder="Enter max price"
                  className="profit-range-edit-input-override"
                  min="0"
                  onChange={updatePricePreview}
                />
                {pricePreview.max && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontWeight: 500 }}>
                    Preview: ₹{pricePreview.max}
                  </div>
                )}
              </Form.Item>

              <Form.Item label="Currency" name="currency" rules={[{ required: true, message: "Please select currency" }]}>
                <Select size="large" placeholder="Select currency" className="profit-range-edit-select-override" showSearch optionFilterProp="children">
                  {countries.map((country) => (
                    <Select.Option key={country._id} value={country.country_currency}>
                      {country.country_currency} ({country.country_name})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Category" name="category" rules={[{ required: true, message: "Please select Category" }]}>
                <Select size="large" placeholder="Select category" className="profit-range-edit-select-override" allowClear>
                  <Option value="basic">Basic</Option>
                  <Option value="premium">Premium</Option>
                  <Option value="luxury">Luxury</Option>
                </Select>
              </Form.Item>
            </div>
          </div>

          <Divider className="profit-range-edit-divider" />

          <Form.Item style={{ marginBottom: 0 }}>
            <div className="profit-range-edit-actions">
              <Button size="large" onClick={onClose} className="profit-range-edit-btn profit-range-edit-btn-cancel">
                Cancel
              </Button>
              <Button
                htmlType="submit"
                size="large"
                icon={<SaveOutlined />}
                className="profit-range-edit-btn profit-range-edit-btn-primary profit-range-edit-btn-override"
              >
                <span style={{ color: "white" }}>{profitRangeData?._id ? "Update Profit Range" : "Add Profit Range"}</span>
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default AgentProfitRangeEditModal;
